import { Flex, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import EFMMonthBlock from "./EFMMonthBlock/EFMMonthBlock";
import ProgressBar from "../../../VAMP/components/MonitoringProgram/ProgressBar/ProgressBar";
import MonitoringProgramEdges from "../../../VAMP/components/MonitoringProgram/MonitoringProgramEdges/MonitoringProgramEdges";
import useMonitoringProgramProgress from "../../hooks/useMonitoringProgramProgress";
import MonitoringProgramStatus from "../../../VAMP/components/MonitoringProgram/ProgramStatus/ProgramStatus";
import SectionHeader from "../../../VAMP/components/SectionHeader/SectionHeader";
import InfoLine from "../../../VAMP/components/InfoLine/InfoLine";
import "../../../VAMP/components/MonitoringProgram/MonitoringProgram.less";
import Images from "../../../../../Images";
import SelectComponent from "../../../../../Components/Select";
import HealthActions from "../../../reducer";
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle";
import PermissionPlaceholder from "../../../components/PermissionPlaceholder";
import useHealthAccessRestricted from "../../../hooks/useHealthAccessRestricted";
import { useIsStripeConnected } from "../../hooks/useIsStripeConnected";
import { buildLockedTimeline } from "../../utils/buildLockedTimeline";
import RegionNotEligiblePlaceholder from "../RegionNotEligiblePlaceholder/RegionNotEligiblePlaceholder";
import cls from "classname";

const statusMessageVariantMap = {
  pending: "neutral",
  compliant: "success",
  warning: "warning",
  under_monitoring: "warning",
  no_status: "neutral",
};

const EFMMonitoringProgram = ({ business_account_id }) => {
  const dispatch = useDispatch();
  const { isMobile } = useSelector((state) => state.nav);
  const { efmMonitoringProgramChart, efmMonitoringProgramChartFetching } = useSelector(
    (state) => state.health
  );
  const isStripeConnected = useIsStripeConnected();
  const rawData = efmMonitoringProgramChart ?? {};
  // Stripe disconnected: hide any (possibly stale) real snapshot history behind a
  // fully-locked timeline instead, matching a brand-new account's empty state.
  const data = isStripeConnected
    ? rawData
    : { ...rawData, current_status: null, timeline: buildLockedTimeline() };
  const currentStatus = data?.current_status;
  const { isShowDemoData } = useDemoDataToggle();
  const isRestricted = useHealthAccessRestricted();

  const yearOptions = useMemo(() => {
    const years = efmMonitoringProgramChart?.available_years ?? [new Date().getFullYear()];
    return years.map((year) => ({ label: year, value: year }));
  }, [efmMonitoringProgramChart]);
  const [year, setYear] = useState(yearOptions?.[yearOptions.length - 1]?.value ?? null);

  useEffect(() => {
    if (!efmMonitoringProgramChart?.available_years?.includes(year)) {
      setYear(yearOptions?.[yearOptions.length - 1]?.value ?? null);
    }
  }, [yearOptions]);

  useEffect(() => {
    if (isStripeConnected) {
      dispatch(
        HealthActions.getEfmMonitoringProgramChartRequest({
          business_account: business_account_id,
          year,
        })
      );
    }
  }, [business_account_id, year, isShowDemoData, isStripeConnected]);

  const {
    isShowProgressBar,
    timeLineSegments,
    startDate,
    endDate,
    edgesIntervalWidth,
    edgesStartOffset,
    isFinishTextHidden,
  } = useMonitoringProgramProgress({ data });

  const startMonth = startDate ? dayjs(startDate).month() + 1 : null;
  const endMonth = endDate ? dayjs(endDate).month() : null;

  const timeline = data?.timeline ?? [];

  const timelineRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < timeline.length; i += 2) {
      rows.push(timeline.slice(i, i + 2));
    }
    return rows;
  }, [timeline]);

  const mobileTimelineItems = useMemo(() => {
    if (!isMobile) return [];

    const items = [];
    let finishInserted = false;

    timelineRows.forEach((row) => {
      const rowStartMonth = row?.[0]?.month;
      const hasStartMonth = startMonth && row?.some((m) => m.month === startMonth);

      if (isShowProgressBar && hasStartMonth) {
        items.push({ type: "marker", markerType: "start" });
      }

      if (isShowProgressBar && !finishInserted && endMonth && rowStartMonth > endMonth) {
        items.push({ type: "marker", markerType: "finish" });
        finishInserted = true;
      }

      items.push({ type: "row", row });
    });

    if (isShowProgressBar && !finishInserted && !isFinishTextHidden) {
      items.push({ type: "marker", markerType: "finish" });
    }

    return items;
  }, [timelineRows, isMobile, isShowProgressBar, startMonth, endMonth, isFinishTextHidden]);

  const isRegionExcluded = data.region === "excluded";

  return (
    <Flex vertical>
      <Spin
        wrapperClassName="monitoring-program-chart-spin"
        spinning={efmMonitoringProgramChartFetching}
      >
        <Flex
          gap={10}
          align="flex-start"
          justify="space-between"
          className="monitoring-program-header"
        >
          <div>
            <SectionHeader
              title="Excessive Fraud Merchant Program (EFM)"
              image={Images.mastercard_3d}
              marginBottom={8}
            />
            {!isRestricted && !isRegionExcluded && currentStatus && (
              <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
                <MonitoringProgramStatus status={currentStatus} />
              </Flex>
            )}
          </div>
          <SelectComponent
            value={year}
            onChange={setYear}
            options={yearOptions}
            style={{ width: isMobile ? "100%" : 100 }}
            className="monitoring-program-year-select"
            disabled={isRestricted}
          />
        </Flex>

        <div
          className={cls("mc-region-gated-content", {
            "is-region-excluded": isRegionExcluded,
          })}
        >
          {!isMobile && isShowProgressBar && (
            <>
              {isRestricted ? (
                <PermissionPlaceholder minHeight={150} />
              ) : (
                <Flex vertical gap={8}>
                  <MonitoringProgramEdges
                    intervalWidth={edgesIntervalWidth}
                    startOffset={edgesStartOffset}
                    isFinishTextHidden={isFinishTextHidden}
                    startDate={startDate}
                    endDate={endDate}
                  />
                  <Flex gap={4} style={{ width: "100%" }}>
                    {timeLineSegments.map((segment, index) => (
                      <ProgressBar key={index} progress={segment.progress} style={{ width: segment.width }} />
                    ))}
                  </Flex>
                </Flex>
              )}
            </>
          )}

          {isMobile ? (
            <div className="monitoring-program-mobile" style={{ marginTop: 16 }}>
              {mobileTimelineItems.map((item, index) => {
                if (item.type === "marker") {
                  const isStart = item.markerType === "start";
                  return (
                    <div key={`marker-${item.markerType}-${index}`} className="monitoring-program-mobile__marker">
                      {isStart
                        ? `${dayjs(startDate).format("D MMMM, YYYY")} - Monitoring program start`
                        : `${dayjs(endDate).format("D MMMM, YYYY")} - Monitoring program finish`}
                    </div>
                  );
                }
                return (
                  <Flex key={`row-${index}`} gap={12} className="monitoring-program-mobile__row">
                    {item.row.map((month) => (
                      <EFMMonthBlock
                        key={month.month}
                        currentMonth={data?.current_month?.month}
                        data={month}
                        styles={{ flex: 1 }}
                        isMobile
                      />
                    ))}
                  </Flex>
                );
              })}
            </div>
          ) : (
            <Flex style={{ marginTop: 12 }}>
              {timeline.map((month) => (
                <EFMMonthBlock
                  key={month.month}
                  currentMonth={data?.current_month?.month}
                  data={month}
                  styles={{ flex: 1 }}
                />
              ))}
            </Flex>
          )}

          {!isRestricted && !isRegionExcluded && currentStatus?.tooltip && (
            <InfoLine
              text={currentStatus.tooltip}
              variant={statusMessageVariantMap[currentStatus?.status] || "neutral"}
              style={{ marginTop: "16px" }}
            />
          )}

          {isRegionExcluded && <RegionNotEligiblePlaceholder programName="EFM" />}
        </div>
      </Spin>
    </Flex>
  );
};

export default EFMMonitoringProgram;
